import { z } from "zod";
/*
 * Warehouse reads a running turn makes through the tenant API. The executor holds no warehouse
 * login: each request carries the run's lease and its warehouse grant, and the tenant derives the
 * read's scope from the run itself (its organisations, subject, capabilities, warehouse profile,
 * lane and bound Twin Logic relations) before its broker reads.
 */
const id = z.string().trim().min(1).max(200);
const lease = {
    runId: id, tenantSlug: id, workerId: id,
    attemptNumber: z.number().int().positive(),
    /** The run's current warehouse-broker execution grant, as the executor holds it after renewal. */
    grant: z.unknown(),
    /**
     * A production Twin Logic view the read is confined to; it must be one the run's own overlays
     * bind. The tenant re-verifies its activation and source grants in the read's transaction.
     */
    productionTwinLogicRelation: z.unknown().optional(),
    /** A governed join's second production view; it too must be one the run's overlays bind. */
    productionTwinLogicJoinedRelation: z.unknown().optional(),
};
const relation = z.object({ schema: z.string().trim().min(1).max(63), table: z.string().trim().min(1).max(200) }).strict();
const scalar = z.union([z.string(), z.number(), z.boolean(), z.null()]);
export const workerWarehouseRequestSchema = z.discriminatedUnion("operation", [
    z.object({
        ...lease, operation: z.literal("listTables"),
        input: z.object({
            schema: z.string().trim().min(1).max(63).optional(), search: z.string().max(500).optional(),
            overfetch: z.boolean().optional(), complete: z.boolean().optional(),
        }).strict(),
    }).strict(),
    z.object({ ...lease, operation: z.literal("describeRelation"), input: relation }).strict(),
    z.object({ ...lease, operation: z.literal("describeTable"), input: relation }).strict(),
    z.object({
        ...lease, operation: z.literal("runQuery"),
        input: z.object({
            sql: z.string().min(1).max(200_000),
            parameters: z.array(z.union([scalar, z.array(scalar)])).max(512).optional(),
            rowLimit: z.number().int().positive().max(10_000).optional(),
            purpose: z.enum(["interactive", "export"]).optional(),
            revisionMode: z.literal("structured_preflight").optional(),
            relationBindingMode: z.literal("canonical_catalog").optional(),
        }).strict(),
    }).strict(),
]);
/** The broker's result for the operation, exactly as `@mlx/warehouse` returns it. */
export const workerWarehouseResponseSchema = z.object({
    operation: z.enum(["listTables", "describeRelation", "describeTable", "runQuery"]),
    result: z.unknown(),
}).strict();
/**
 * A read the broker refused or could not complete, as the executor's tools handle it: the error's
 * class, message and structured diagnostics (a SQL revision request carries the relations' schemas).
 */
export const workerWarehouseErrorSchema = z.object({
    kind: z.enum(["warehouse", "sql_revision_required", "relation_binding_required"]),
    message: z.string(),
    code: z.string().optional(),
    diagnostic: z.record(z.string(), z.unknown()).optional(),
    relationSchemas: z.array(z.unknown()).optional(),
}).strict();
export const WORKER_WAREHOUSE_REFUSALS = {
    leaseNotHeld: "warehouse_lease_not_held",
    grantInvalid: "warehouse_grant_invalid",
    relationNotBound: "warehouse_relation_not_bound",
    readFailed: "warehouse_read_failed",
};
